import classNames from 'classnames';
import _ from 'lodash';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HeatmapBucket,
  HeatmapBucketSortDirection,
  HeatmapBucketStatField,
} from '../common/heatmap.helper';
import { HeatmapMetricDescriptor } from '../common/heatmap.types';
import ButtonToolbar from '../components/actions/ButtonToolbar';
import { ButtonToolbarGroup } from '../components/actions/ButtonToolbarGroup';
import DropdownSelector from '../components/actions/DropdownSelector';
import * as Icon from '../components/icons';
import LoadingIndicator from '../components/LoadingIndicator';
import TitleToolbarContainer from '../containers/TitleToolbarContainer';
import { HeatmapFilter } from '../simulation/store/simulation.types';
import HeatmapLegendBuckets, {
  HeatmapLegendBucketsProps,
} from '../simulation/toolbars/HeatmapLegendBuckets';

export type HeatmapToolbarProps = {
  metricDescriptors: Record<
    string,
    HeatmapMetricDescriptor<string, Record<string, any>>
  >;
  metricDescriptor: HeatmapMetricDescriptor<string, Record<string, any>>;
  onMetricChange: (v: string) => void;
  buckets: HeatmapBucket[];
  hasStats: boolean;

  heatmapFilter: HeatmapFilter;
  onHeatmapFilterChange: (value: HeatmapFilter) => void;
  sortBy: HeatmapBucketStatField;
  onSortByChange: (value: HeatmapBucketStatField) => void;

  sortDirection: HeatmapBucketSortDirection;
  onSortDirectionChange: (value: HeatmapBucketSortDirection) => void;

  onBucketActionClick: (bucket: HeatmapBucket) => void;
  //helpNavTo?: HelpNavTo<HelpIndex>; // {'actuality/actuality-kpi'}
  hasPolicyMode?: boolean;
  policyMode?: boolean;
  onPolicyModeChange?: (v: boolean) => void;
};

const HeatmapToolbar: React.FC<HeatmapToolbarProps> = props => {
  const { t } = useTranslation('simulation');

  const bucketProps: HeatmapLegendBucketsProps = {
    title: props.metricDescriptor?.title,
    buckets: props.buckets ?? [],
    metric: props.metricDescriptor,
    heatmapFilter: props.heatmapFilter,
    setHeatmapFilter: props.onHeatmapFilterChange,
    sortDirection: props.sortDirection,
    onSortDirectionChange: props.onSortDirectionChange,
  };

  if (!bucketProps.title || !bucketProps.buckets.length) {
    console.warn('Invalid bucketProps', bucketProps);
    return null; // or return a fallback component
  }

  const showHeatmapSelectors = !props.hasPolicyMode || !props.policyMode;
  return (
    <ButtonToolbarGroup
      className={classNames(
        'z-toolbar absolute top-1 flex flex-col ltr:left-1 rtl:right-1',
      )}
    >
      <TitleToolbarContainer
        id="panel-toolbar-analytics"
        title={t`Analytics`}
        collapsible
      >
        <div className="flex">
          <ButtonToolbar
            titleTrace={`Policy Mode`}
            isSelected={props.policyMode}
            onChange={() => props.onPolicyModeChange(true)}
          >
            <Icon.PolicyAllocationRequirement
              className={classNames('h-8 w-8')}
              alt={t`Policy Mode`}
              title={t`Policy Mode`}
            />
          </ButtonToolbar>

          <ButtonToolbar
            titleTrace={`Heatmap Mode`}
            isSelected={!props.policyMode}
            onChange={() => props.onPolicyModeChange(false)}
          >
            <Icon.HeatMap9
              className={classNames('h-8 w-8')}
              alt={t`Heatmap Mode`}
              title={t`Heatmap Mode`}
            />
          </ButtonToolbar>
        </div>
      </TitleToolbarContainer>
      {showHeatmapSelectors && (
        <>
          <TitleToolbarContainer id="panel-toolbar-analytics" title={t`Metric`}>
            <div className="flex w-full items-center px-px">
              <DropdownSelector
                DropAlignRight
                className="w-full text-sm"
                classNameValue="text-menu-text/70 bg-menu/70 p-1 hover:text-menu-text"
                value={props.metricDescriptor.type}
                values={_.map(props.metricDescriptors, d => d.type)}
                renderValue={v => props.metricDescriptors[v].title}
                onChange={(v, e) => {
                  e.stopPropagation();
                  setTimeout(() => {
                    props.onMetricChange(v);
                  }, 100);
                }}
                hasSearch
              />
            </div>
          </TitleToolbarContainer>

          <Suspense
            fallback={
              <LoadingIndicator
                noColor
                transparent
                className={classNames('text-minimal', 'opacity-50', 'mb-2')}
                message={`${t`Loading`}...`}
                circleSize={32}
              />
            }
          >
            <HeatmapLegendBuckets {...bucketProps} />
          </Suspense>
        </>
      )}
    </ButtonToolbarGroup>
  );
};

export default HeatmapToolbar;
